import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from "@egaranti/components";

import CategoryCombobox, {
  isNewValue,
  newValueName,
} from "@/components/shared/CategoryCombobox";
import { zodResolver } from "@hookform/resolvers/zod";

import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ProductService from "@/services/productService";

import SerialNumberInput from "@/components/products/serialNumberInput";
import { Breadcrumb, BreadcrumbItem } from "@/components/shared/breadcrumbs";
import CheckRole from "@/components/shared/checkRole";
import ImageBlock from "@/components/shared/ImageBlock";

import { Loader2 } from "lucide-react";
import * as z from "zod";

// EPS-4867 / panel create↔edit parite:
// Ürün OLUŞTURMA ekranı, DÜZENLEME ekranı ile aynı bölümleri içerir.
// Edit zamanla Videolar, Kategori Seri No Bilgisi, ayrı görsel kartları ve
// Belgeler bölümlerini aldı; create geride kalmıştı (Abdullah Bey 23 Tem
// canlı gösterdi). Bu ekran edit'in yapısını birebir izler:
//   Ürün Kategorisi (2×2) → Ana/Kategori+SeriNo/Alt/Marka Görseli kartları →
//   Ürün Bilgisi → Videolar → Seri Numaraları → Belgeler.
// Görsel + video + kategori seri-no işlemleri kategori/marka id'sine bağlı,
// ürün id'si gerektirmez → create'te de çalışır. Belgeler ürün id'si istediği
// için, Kaydet sonrası dönen id ile düzenleme ekranına yönlendirilir; belge
// oradan eklenir (edit ile birebir devam).

const ProductCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);

  // Cascade data (4 levels)
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);

  // Image state per level
  const [mainCategoryImageUrl, setMainCategoryImageUrl] = useState("");
  const [categoryImageUrl, setCategoryImageUrl] = useState("");
  const [subCategoryImageUrl, setSubCategoryImageUrl] = useState("");
  const [brandImageUrl, setBrandImageUrl] = useState("");
  const [productImageUrl, setProductImageUrl] = useState("");
  // Ürün görseli BE'de POST/PUT payload'undaki imageUrl ile DEĞİL, ürün id'si
  // gerektiren ayrı multipart endpoint (postProductImage) ile kaydedilir.
  // Bu yüzden dosyanın kendisini saklarız; kayıttan sonra dönen id ile yollanır.
  const [productImageFile, setProductImageFile] = useState(null);
  const [busy, setBusy] = useState({
    main: false,
    cat: false,
    sub: false,
    brand: false,
    product: false,
  });
  const mainImgRef = useRef(null);
  const catImgRef = useRef(null);
  const subImgRef = useRef(null);
  const brandImgRef = useRef(null);
  const productImgRef = useRef(null);

  // Kategori seri no bilgisi (kategori seviyesinde, kategori id'ye bağlı — edit ile aynı)
  const [categorySerialNoInfo, setCategorySerialNoInfo] = useState("");
  const [serialNoInfoSaving, setSerialNoInfoSaving] = useState(false);

  const formSchema = z.object({
    mainCategoryId: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    categoryId: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    subCategoryId: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    brandId: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    name: z.string().min(1, { message: t("common.validateMsg.requiredField") }),
    model: z.string().optional().nullable(),
    // Edit ile birebir: garanti süresi AY olarak girilir (yıl dropdown değil).
    // Eski create "Yıl" dropdown gösteriyordu ama BE değeri ay olarak
    // kaydediyordu (2 "Yıl" → listede "2 ay") — bu tutarsızlık giderildi.
    warrantyDuration: z
      .number({ required_error: t("common.validateMsg.requiredField") })
      .min(1, { message: t("common.validateMsg.requiredField") }),
    price: z
      .number({ required_error: t("common.validateMsg.requiredField") })
      .min(0, {
        message: t(
          "productsPage.priceMinError",
          "Fiyat 0 veya daha büyük olmalıdır",
        ),
      }),
    priceUnit: z
      .string()
      .min(1, { message: t("common.validateMsg.requiredField") }),
    additionalService: z.boolean(),
    setupRequired: z.boolean(),
    description: z.string().optional().nullable(),
    serialNumber: z.array(z.string()).optional().default([]),
    // Videolar edit ile aynı: form state, createProduct payload'ıyla gider.
    merchantProductVideoList: z
      .array(
        z.object({
          videoTitle: z.string().optional().default(""),
          // Video linki opsiyonel; ama girildiyse geçerli bir http(s) URL olmalı
          // (eskiden herhangi bir metin kabul ediliyordu — bozuk link kaydolabiliyordu).
          videoURL: z
            .string()
            .optional()
            .default("")
            .refine((v) => !v || /^https?:\/\/\S+$/i.test(v), {
              message: t(
                "productsPage.videoUrlInvalid",
                "Geçerli bir video linki girin (http:// veya https:// ile başlamalı)",
              ),
            }),
        }),
      )
      .optional()
      .default([]),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mainCategoryId: "",
      categoryId: "",
      subCategoryId: "",
      brandId: "",
      name: "",
      model: "",
      warrantyDuration: 24,
      price: undefined,
      priceUnit: "TRY",
      additionalService: false,
      setupRequired: false,
      description: "",
      serialNumber: [],
      merchantProductVideoList: [],
    },
  });

  const mainCategoryId = form.watch("mainCategoryId");
  const categoryId = form.watch("categoryId");
  const subCategoryId = form.watch("subCategoryId");
  const brandId = form.watch("brandId");
  const videos = form.watch("merchantProductVideoList");

  // Load main categories on mount
  useEffect(() => {
    let cancelled = false;
    setLoadingMain(true);
    ProductService.getMainCategories()
      .then((res) => {
        if (!cancelled) setMainCategories(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setMainCategories([]);
        toast({
          variant: "error",
          title:
            err?.response?.data?.errors?.[0]?.message ||
            t(
              "productsPage.categoriesLoadError",
              "Kategoriler yüklenemedi. Tekrar denemek için sayfayı yenileyin.",
            ),
        });
      })
      .finally(() => !cancelled && setLoadingMain(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When main category changes, fetch its image and reset ALL child state
  useEffect(() => {
    if (!mainCategoryId) {
      setMainCategoryImageUrl("");
      setCategories([]);
      setSubCategories([]);
      setBrands([]);
      form.setValue("categoryId", "");
      form.setValue("subCategoryId", "");
      form.setValue("brandId", "");
      setCategoryImageUrl("");
      setSubCategoryImageUrl("");
      setCategorySerialNoInfo("");
      return;
    }
    const selected = mainCategories.find(
      (c) => String(c.id) === String(mainCategoryId),
    );
    setMainCategoryImageUrl(selected?.imageUrl || "");
    form.setValue("categoryId", "");
    form.setValue("subCategoryId", "");
    form.setValue("brandId", "");
    setCategoryImageUrl("");
    setSubCategoryImageUrl("");
    setCategorySerialNoInfo("");
    setSubCategories([]);
    setBrands([]);

    // Yeni (henüz oluşturulmamış) ana kategorinin alt kaydı yoktur — fetch etme.
    if (isNewValue(mainCategoryId)) {
      setCategories([]);
      return;
    }

    let cancelled = false;
    setLoadingCats(true);
    ProductService.getCategories(mainCategoryId)
      .then((res) => {
        if (!cancelled) setCategories(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setCategories([]);
        toast({
          variant: "error",
          title:
            err?.response?.data?.errors?.[0]?.message ||
            t(
              "productsPage.subCategoriesLoadError",
              "Alt kategoriler yüklenemedi. Tekrar denemek için kategoriyi yeniden seçin.",
            ),
        });
      })
      .finally(() => !cancelled && setLoadingCats(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCategoryId, mainCategories]);

  // When category changes, fetch its image + serial-no info + sub-categories, reset brand
  useEffect(() => {
    if (!categoryId) {
      setCategoryImageUrl("");
      setCategorySerialNoInfo("");
      setSubCategories([]);
      setBrands([]);
      form.setValue("subCategoryId", "");
      form.setValue("brandId", "");
      setSubCategoryImageUrl("");
      return;
    }
    const selected = categories.find(
      (c) => String(c.id) === String(categoryId),
    );
    setCategoryImageUrl(selected?.imageUrl || "");
    // Edit ile aynı: seri no bilgisi kategori nesnesinden gelir.
    setCategorySerialNoInfo(selected?.serialNoInfo || "");
    form.setValue("subCategoryId", "");
    form.setValue("brandId", "");
    setSubCategoryImageUrl("");
    setBrands([]);

    // Yeni kategorinin alt kategorisi yoktur — fetch etme.
    if (isNewValue(categoryId)) {
      setSubCategories([]);
      return;
    }

    let cancelled = false;
    setLoadingSubs(true);
    ProductService.getSubCategories(categoryId)
      .then((res) => {
        if (!cancelled) setSubCategories(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setSubCategories([]);
        toast({
          variant: "error",
          title:
            err?.response?.data?.errors?.[0]?.message ||
            t(
              "productsPage.subCategoriesLevel3LoadError",
              "Alt kategoriler yüklenemedi. Tekrar denemek için kategoriyi yeniden seçin.",
            ),
        });
      })
      .finally(() => !cancelled && setLoadingSubs(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, categories]);

  // When sub-category changes, fetch its image + brands list
  useEffect(() => {
    if (!subCategoryId) {
      setSubCategoryImageUrl("");
      setBrandImageUrl("");
      setBrands([]);
      form.setValue("brandId", "");
      return;
    }
    const selected = subCategories.find(
      (s) => String(s.id) === String(subCategoryId),
    );
    setSubCategoryImageUrl(selected?.imageUrl || "");
    setBrandImageUrl("");
    form.setValue("brandId", "");

    // Yeni alt kategorinin markası yoktur — fetch etme.
    if (isNewValue(subCategoryId)) {
      setBrands([]);
      return;
    }

    let cancelled = false;
    setLoadingBrands(true);
    ProductService.getSubCategoryBrands(subCategoryId)
      .then((res) => {
        if (!cancelled) setBrands(res?.data || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setBrands([]);
        toast({
          variant: "error",
          title:
            err?.response?.data?.errors?.[0]?.message ||
            t(
              "productsPage.brandsLoadError",
              "Markalar yüklenemedi. Tekrar denemek için alt kategoriyi yeniden seçin.",
            ),
        });
      })
      .finally(() => !cancelled && setLoadingBrands(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryId, subCategories]);

  // When brand changes, fetch its image from brands list
  useEffect(() => {
    if (!brandId) {
      setBrandImageUrl("");
      return;
    }
    const selected = brands.find((b) => String(b.id) === String(brandId));
    setBrandImageUrl(selected?.imageUrl || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, brands]);

  // Generic image upload handler
  const handleImageUpload = async (level, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    // Edit ile aynı: 40 MB üstü görsel reddedilir.
    const MAX_FILE_SIZE = 40 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast({
        variant: "error",
        title: t("productsPage.fileSizeError", "Dosya boyutu çok büyük"),
        description: t(
          "productsPage.fileSizeErrorDesc",
          "Dosya boyutu maksimum 40 MB olmalıdır.",
        ),
      });
      return;
    }

    setBusy((b) => ({ ...b, [level]: true }));
    try {
      const uploadRes = await ProductService.uploadFile(file);
      const url = uploadRes?.url || uploadRes?.data?.url;
      if (!url) throw new Error("upload-url-missing");

      if (level === "main") {
        await ProductService.updateMainCategoryImage(mainCategoryId, url);
        setMainCategoryImageUrl(url);
      } else if (level === "cat") {
        await ProductService.updateCategoryImage(categoryId, url);
        setCategoryImageUrl(url);
      } else if (level === "sub") {
        await ProductService.updateSubCategoryImage(subCategoryId, url);
        setSubCategoryImageUrl(url);
      } else if (level === "brand") {
        await ProductService.updateSubCategoryBrandImage(brandId, url);
        setBrandImageUrl(url);
      } else if (level === "product") {
        // Ürün henüz persist edilmedi (productId yok). Önizleme için yüklenen
        // URL'i göster; asıl kayıt için DOSYAYI sakla — BE ürün görselini yalnız
        // postProductImage (ürün id'li multipart) ile kaydediyor, payload'daki
        // imageUrl'i yok sayıyor. Kayıt sonrası dönen id ile yollanacak.
        setProductImageUrl(url);
        setProductImageFile(file);
      }
      toast({
        title: t("productsPage.imageUploadSuccess", "Görsel güncellendi"),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.imageUploadError", "Görsel yüklenemedi"),
      });
    } finally {
      setBusy((b) => ({ ...b, [level]: false }));
    }
  };

  // EPS-4850 B-10 fix: ID yokken hiçbir API çağrılmıyordu ama success toast yine
  // basılıyordu — kullanıcı silinmiş gibi sanıyordu. Branch'lerin her biri
  // kendi başarı toast'ını basar; ID yoksa sessiz no-op + early return.
  const handleImageDelete = async (level) => {
    setBusy((b) => ({ ...b, [level]: true }));
    try {
      if (level === "main") {
        if (!mainCategoryId) return;
        await ProductService.deleteMainCategoryImage(mainCategoryId);
        setMainCategoryImageUrl("");
      } else if (level === "cat") {
        if (!categoryId) return;
        await ProductService.deleteCategoryImage(categoryId);
        setCategoryImageUrl("");
      } else if (level === "sub") {
        if (!subCategoryId) return;
        await ProductService.deleteSubCategoryImage(subCategoryId);
        setSubCategoryImageUrl("");
      } else if (level === "brand") {
        if (!brandId) return;
        await ProductService.deleteSubCategoryBrandImage(brandId);
        setBrandImageUrl("");
      } else if (level === "product") {
        setProductImageUrl("");
        setProductImageFile(null);
      } else {
        return;
      }
      toast({
        title: t("productsPage.imageDeleteSuccess", "Görsel silindi"),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.imageDeleteError", "Görsel silinemedi"),
      });
    } finally {
      setBusy((b) => ({ ...b, [level]: false }));
    }
  };

  // Kategori seri no bilgisi kaydet (kategori id'ye bağlı — edit ile aynı).
  const handleCategorySerialNoInfoSave = async () => {
    if (!categoryId || isNewValue(categoryId)) return;
    setSerialNoInfoSaving(true);
    try {
      const updated = await ProductService.updateCategorySerialNoInfo(
        categoryId,
        categorySerialNoInfo,
      );
      setCategorySerialNoInfo(updated?.serialNoInfo ?? categorySerialNoInfo);
      toast({
        title: t(
          "productsPage.categorySerialNoInfoUpdateSuccess",
          "Seri no bilgisi güncellendi",
        ),
        variant: "success",
      });
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t(
            "productsPage.categorySerialNoInfoUpdateError",
            "Seri no bilgisi güncellenemedi",
          ),
      });
    } finally {
      setSerialNoInfoSaving(false);
    }
  };

  // Video handler'ları — edit ile birebir (form state).
  const handleAddVideo = () => {
    const updated = [...(videos || []), { videoTitle: "", videoURL: "" }];
    form.setValue("merchantProductVideoList", updated);
  };

  const handleDeleteVideo = (index) => {
    const updated = (videos || []).filter((_, i) => i !== index);
    form.setValue("merchantProductVideoList", updated);
  };

  const handleVideoChange = (index, field, value) => {
    const updated = [...(videos || [])];
    updated[index] = { ...updated[index], [field]: value };
    form.setValue("merchantProductVideoList", updated);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // BE name lookups — kullanıcının seçtiği/yazdığı 4 seviye ad olarak gönderilir.
      // Var olan kayıt seçildiyse listeden adı çözeriz; yeni yazıldıysa (new::<ad>)
      // doğrudan o adı göndeririz — BE crateCategoryIfNotExists yoksa oluşturur.
      const nameOf = (val, list) =>
        isNewValue(val)
          ? newValueName(val)
          : list.find((x) => String(x.id) === String(val))?.name || "";
      const payload = {
        ...data,
        mainCategoryName: nameOf(data.mainCategoryId, mainCategories),
        categoryName: nameOf(data.categoryId, categories),
        subCategoryName: nameOf(data.subCategoryId, subCategories),
        brandName: nameOf(data.brandId, brands),
        imageUrl: productImageUrl || "",
        serialNumber:
          data.serialNumber?.length === 1 && data.serialNumber[0] === ""
            ? []
            : data.serialNumber || [],
        // Boş video satırlarını at (title+url ikisi de boşsa gönderme).
        merchantProductVideoList: (data.merchantProductVideoList || []).filter(
          (v) => (v.videoTitle || "").trim() || (v.videoURL || "").trim(),
        ),
      };
      delete payload.mainCategoryId;
      delete payload.categoryId;
      delete payload.subCategoryId;
      delete payload.brandId;

      const res = await ProductService.createProduct(payload);
      const newId = res?.data?.id;

      // Video ve ürün görseli create'te İKİ AŞAMALI kaydediliyor: önce ürün
      // POST edilir, sonra ayrı bir istek (PUT / multipart) ile bunlar yazılır —
      // çünkü BE bunları POST payload'ında yok sayıyor. Bu ikinci istek geçici
      // bir ağ/gateway dalgalanmasına denk gelirse (net::ERR_FAILED) veri sessizce
      // kaybolur. Bu yüzden kısa gecikmeli 3 denemelik retry uygularız.
      const withRetry = async (fn, attempts = 3) => {
        let lastErr;
        for (let i = 0; i < attempts; i++) {
          try {
            return await fn();
          } catch (e) {
            lastErr = e;
            await new Promise((r) => setTimeout(r, 600 * (i + 1)));
          }
        }
        throw lastErr;
      };

      // Video: oluşan id ile update çağırıp persist et.
      // KRİTİK: serialNumber'ı GÖNDERME. BE updateById, serialNumbers non-null
      // ise merchant_product_serial'da delete+insert yapıyor; seri no'lar zaten
      // POST'ta kaydedildiği için bu, aynı ürünün serial satırlarında lock
      // çakışmasına yol açıp transaction'ı takıyor (orphan idle-in-transaction →
      // lock birikmesi → servis çöküyordu). serialNumber:null ile BE serial
      // bloğunu atlar; mevcut seri no'lar korunur, sadece video yazılır.
      if (newId && payload.merchantProductVideoList.length > 0) {
        try {
          await withRetry(() =>
            ProductService.updateProduct({
              ...payload,
              id: newId,
              serialNumber: null,
            }),
          );
        } catch {
          toast({
            variant: "error",
            title: t(
              "productsPage.videoSaveAfterCreateError",
              "Ürün oluşturuldu ancak video kaydedilemedi. Düzenleme ekranından tekrar deneyebilirsiniz.",
            ),
          });
        }
      }

      // Ürün görseli: ürün id'li ayrı multipart endpoint (postProductImage).
      if (newId && productImageFile) {
        try {
          await withRetry(() => {
            const fd = new FormData();
            fd.append("file", productImageFile);
            return ProductService.postProductImage(newId, fd);
          });
        } catch {
          toast({
            variant: "error",
            title: t(
              "productsPage.productImageSaveAfterCreateError",
              "Ürün oluşturuldu ancak ürün görseli kaydedilemedi. Düzenleme ekranından tekrar deneyebilirsiniz.",
            ),
          });
        }
      }

      // Belgeler ürün id'si gerektirir — oluşan ürünün düzenleme ekranına git,
      // Garanti Belgesi / Kullanım Kılavuzu oradan eklenir (edit ile birebir).
      toast({
        title: t("productsPage.createSuccess", "Ürün oluşturuldu"),
        variant: "success",
      });
      navigate(newId ? `/products/products/${newId}` : "/products/products");
    } catch (err) {
      toast({
        variant: "error",
        title:
          err?.response?.data?.errors?.[0]?.message ||
          t("productsPage.createError", "Ürün oluşturulamadı"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F9FAFC] pb-32">
      <Breadcrumb>
        <BreadcrumbItem link="/products/products">
          {t("productsPage.title", "Ürünler")}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t("productsPage.addProduct", "Yeni Ürün")}
        </BreadcrumbItem>
      </Breadcrumb>

      <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-6">
        <h1 className="mb-6 text-2xl font-semibold text-[#111729]">
          {t("productsPage.addProduct", "Yeni Ürün")}
        </h1>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Ürün Kategorisi — 2×2 grid (edit ile aynı) */}
            <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
              <h2 className="mb-4 text-lg font-medium">
                {t("productsPage.productCategory", "Ürün Kategorisi")}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="mainCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.mainCategory", "Ana Kategori")} *
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={mainCategories}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={loadingMain}
                          loading={loadingMain}
                          placeholder={t(
                            "productsPage.selectMainCategory",
                            "Ana kategori seçin",
                          )}
                          ariaLabel={t(
                            "productsPage.mainCategory",
                            "Ana Kategori",
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.category", "Kategori")} *
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={categories}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!mainCategoryId || loadingCats}
                          loading={loadingCats}
                          placeholder={
                            !mainCategoryId
                              ? t(
                                  "productsPage.selectMainCategoryFirst",
                                  "Önce ana kategori seçin",
                                )
                              : t("productsPage.selectCategory", "Kategori seçin")
                          }
                          ariaLabel={t("productsPage.category", "Kategori")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="subCategoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.subCategory", "Alt Kategori")} *
                      </FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={subCategories}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!categoryId || loadingSubs}
                          loading={loadingSubs}
                          placeholder={
                            !categoryId
                              ? t(
                                  "productsPage.selectCategoryFirst",
                                  "Önce kategori seçin",
                                )
                              : t(
                                  "productsPage.selectSubCategory",
                                  "Alt kategori seçin",
                                )
                          }
                          ariaLabel={t(
                            "productsPage.subCategory",
                            "Alt Kategori",
                          )}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="brandId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("productsPage.brand", "Marka")} *</FormLabel>
                      <FormControl>
                        <CategoryCombobox
                          items={brands}
                          value={field.value}
                          onChange={field.onChange}
                          disabled={!subCategoryId || loadingBrands}
                          loading={loadingBrands}
                          placeholder={
                            !subCategoryId
                              ? t(
                                  "productsPage.selectSubCategoryFirst",
                                  "Önce alt kategori seçin",
                                )
                              : t("productsPage.selectBrand", "Marka seçin")
                          }
                          ariaLabel={t("productsPage.brand", "Marka")}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            {/* Ana Kategori Görseli — ayrı kart (edit ile aynı) */}
            {mainCategoryId && !isNewValue(mainCategoryId) ? (
              <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
                <h2 className="mb-3 text-lg font-medium">
                  {t("productsPage.mainCategoryImageTitle", "Ana Kategori Görseli")}
                </h2>
                <div className="mb-4 rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.mainCategoryImageNote",
                    "Bu görsel seçilen ana kategori için ortaktır; aynı ana kategoriyi kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>
                <ImageBlock
                  t={t}
                  levelLabel={t("productsPage.mainCategory", "Ana Kategori")}
                  url={mainCategoryImageUrl}
                  busyFlag={busy.main}
                  inputRef={mainImgRef}
                  onUpload={(e) => handleImageUpload("main", e)}
                  onDelete={() => handleImageDelete("main")}
                />
              </section>
            ) : null}

            {/* Kategori Görseli ve Seri No Bilgisi — ayrı kart (edit ile aynı) */}
            {categoryId && !isNewValue(categoryId) ? (
              <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
                <h2 className="mb-3 text-lg font-medium">
                  {t(
                    "productsPage.categoryImageAndSerialTitle",
                    "Kategori Görseli ve Seri No Bilgisi",
                  )}
                </h2>
                <div className="mb-4 rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.categoryImageAndSerialNote",
                    "Bu görsel ve seri no bilgisi seçilen kategori için ortaktır; aynı kategoriyi kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <ImageBlock
                    t={t}
                    levelLabel={t("productsPage.categoryImage", "Kategori Görseli")}
                    url={categoryImageUrl}
                    busyFlag={busy.cat}
                    inputRef={catImgRef}
                    onUpload={(e) => handleImageUpload("cat", e)}
                    onDelete={() => handleImageDelete("cat")}
                  />
                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="categorySerialNoInfo"
                      className="text-sm font-medium text-gray-900"
                    >
                      {t("productsPage.categorySerialNoInfo", "Seri No Bilgisi")}
                    </label>
                    <Textarea
                      id="categorySerialNoInfo"
                      value={categorySerialNoInfo}
                      onChange={(e) => setCategorySerialNoInfo(e.target.value)}
                      placeholder={t(
                        "productsPage.categorySerialNoInfoPlaceholder",
                        "Örn: Cihazın arkasında IMEI olarak yazar",
                      )}
                      rows={3}
                    />
                    <Button
                      type="button"
                      onClick={handleCategorySerialNoInfoSave}
                      disabled={serialNoInfoSaving}
                      className="self-start"
                    >
                      {serialNoInfoSaving
                        ? t("common.saving", "Kaydediliyor...")
                        : t(
                            "productsPage.saveCategorySerialNoInfo",
                            "Seri No Bilgisini Kaydet",
                          )}
                    </Button>
                  </div>
                </div>
              </section>
            ) : null}

            {/* Alt Kategori Görseli — ayrı kart (edit ile aynı) */}
            {subCategoryId && !isNewValue(subCategoryId) ? (
              <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
                <h2 className="mb-3 text-lg font-medium">
                  {t("productsPage.subCategoryImageTitle", "Alt Kategori Görseli")}
                </h2>
                <div className="mb-4 rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.subCategoryImageNote",
                    "Bu görsel seçilen alt kategori için ortaktır; aynı alt kategoriyi kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>
                <ImageBlock
                  t={t}
                  levelLabel={t("productsPage.subCategory", "Alt Kategori")}
                  url={subCategoryImageUrl}
                  busyFlag={busy.sub}
                  inputRef={subImgRef}
                  onUpload={(e) => handleImageUpload("sub", e)}
                  onDelete={() => handleImageDelete("sub")}
                />
              </section>
            ) : null}

            {/* Marka Görseli — ayrı kart (edit ile aynı) */}
            {brandId && !isNewValue(brandId) ? (
              <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
                <h2 className="mb-3 text-lg font-medium">
                  {t("productsPage.brandImageTitle", "Marka Görseli")}
                </h2>
                <div className="mb-4 rounded-md border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    "productsPage.brandImageNote",
                    "Bu görsel seçilen marka için ortaktır; aynı markayı kullanan tüm ürünlerde gösterilir.",
                  )}
                </div>
                <ImageBlock
                  t={t}
                  levelLabel={t("productsPage.brand", "Marka")}
                  url={brandImageUrl}
                  busyFlag={busy.brand}
                  inputRef={brandImgRef}
                  onUpload={(e) => handleImageUpload("brand", e)}
                  onDelete={() => handleImageDelete("brand")}
                />
              </section>
            ) : null}

            {/* Ürün Bilgisi (edit ile aynı: alanlar inline, garanti AY olarak) */}
            <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
              <h2 className="mb-4 text-lg font-medium">
                {t("productsPage.productInfo", "Ürün Bilgisi")}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.productName", "Ürün Adı")} *
                      </FormLabel>
                      <FormControl>
                        <Input maxLength={70} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="model"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.productModel", "Model")}
                      </FormLabel>
                      <FormControl>
                        <Input maxLength={70} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="warrantyDuration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.warrantyDuration", "Garanti Süresi (Ay)")}{" "}
                        *
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            field.onChange(v === "" ? undefined : Number(v));
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        {t("productsPage.warrantyDurationDesc", "Ay olarak giriniz")}
                      </FormDescription>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.description", "Açıklama")}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          rows={1}
                          maxLength={1000}
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="priceUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("productsPage.priceUnit", "Para Birimi")} *
                      </FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="TRY">TRY (₺)</SelectItem>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="EUR">EUR (€)</SelectItem>
                          <SelectItem value="GBP">GBP (£)</SelectItem>
                          <SelectItem value="UZS">UZS</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("productsPage.price", "Fiyat")} *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          placeholder="0"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            field.onChange(v === "" ? undefined : Number(v));
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="md:col-span-2">
                  <h4 className="mb-2 block text-sm font-medium leading-none text-[#111729]">
                    {t("productsPage.productImage", "Ürün Görseli")}
                  </h4>
                  {/* Edit ile aynı: ürün görseli yoksa miras alınan görsel
                      (alt kategori → kategori → ana kategori) önizlemede gösterilir. */}
                  {!productImageUrl &&
                  (subCategoryImageUrl || categoryImageUrl || mainCategoryImageUrl) ? (
                    <div className="mb-3 flex flex-wrap items-start gap-3 rounded-md border bg-gray-50 p-3">
                      <img
                        src={
                          subCategoryImageUrl ||
                          categoryImageUrl ||
                          mainCategoryImageUrl
                        }
                        alt={t("productsPage.productImage", "Ürün Görseli")}
                        className="h-20 w-20 rounded-md border object-cover"
                      />
                      <p className="text-xs text-gray-500">
                        {`${
                          subCategoryImageUrl
                            ? t("productsPage.subCategory", "Alt Kategori")
                            : categoryImageUrl
                              ? t("productsPage.category", "Kategori")
                              : t("productsPage.mainCategory", "Ana Kategori")
                        } görseli kullanılıyor. Kendi görselinizi yüklemek için aşağıdan dosya seçin.`}
                      </p>
                    </div>
                  ) : null}
                  <ImageBlock
                    t={t}
                    levelLabel={t("productsPage.productImage", "Ürün Görseli")}
                    url={productImageUrl}
                    busyFlag={busy.product}
                    inputRef={productImgRef}
                    onUpload={(e) => handleImageUpload("product", e)}
                    onDelete={() => handleImageDelete("product")}
                    hint={t(
                      "productsPage.productImageHint",
                      "Yüklemezseniz sırasıyla alt kategori → kategori → ana kategori görseli kullanılır.",
                    )}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-6 md:col-span-2">
                  <FormField
                    control={form.control}
                    name="setupRequired"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-1 py-2">
                        <FormLabel>
                          {t("productsPage.installationRequired", "Kurulum Gerekli")}
                        </FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <hr className="hidden h-12 w-px bg-[#E0E0E0] sm:block" />
                  <FormField
                    control={form.control}
                    name="additionalService"
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-1 py-2">
                        <FormLabel>
                          {t("productsPage.additionalService", "Ek Hizmet Gerekli")}
                        </FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </section>

            {/* Videolar (edit ile aynı) */}
            <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
              <h2 className="mb-4 text-lg font-medium">
                {t("productsPage.productVideos", "Videolar")}
              </h2>
              {(videos || []).map((video, index) => (
                <div
                  key={`video-${index}`}
                  className="mb-4 grid grid-cols-1 items-center gap-4 border-b pb-6 sm:grid-cols-2"
                >
                  <FormField
                    control={form.control}
                    name={`merchantProductVideoList[${index}].videoTitle`}
                    render={() => (
                      <FormItem>
                        <FormLabel>
                          {t("productsPage.videoTitle", "Video Başlığı")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            value={video.videoTitle || ""}
                            onChange={(e) =>
                              handleVideoChange(
                                index,
                                "videoTitle",
                                e.target.value,
                              )
                            }
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`merchantProductVideoList[${index}].videoURL`}
                    render={() => (
                      <FormItem>
                        <FormLabel>
                          {t("productsPage.videoURL", "Video URL")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            value={video.videoURL || ""}
                            onChange={(e) =>
                              handleVideoChange(index, "videoURL", e.target.value)
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <button
                    type="button"
                    className="col-span-2 text-right font-medium text-[#F04438]"
                    onClick={() => handleDeleteVideo(index)}
                  >
                    {t("productsPage.deleteVideo", "Videoyu Sil")}
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="self-start font-medium text-[#0040C9]"
                onClick={handleAddVideo}
              >
                {t("productsPage.addVideo", "Video Ekle")}
              </button>
            </section>

            {/* Seri Numaraları — SerialNumberInput kendi kartını + "Seri Numara
                Ayarları" başlığını çiziyor; edit ile aynı, ekstra başlık sarma. */}
            <SerialNumberInput form={form} />

            {/* Belgeler — ürün id gerektirir; kaydettikten sonra düzenleme ekranından eklenir */}
            <section className="rounded-lg bg-white p-5 shadow-sm md:p-6">
              <h2 className="mb-3 text-lg font-medium">
                {t("productsPage.documentInfo", "Ürüne ait belgeler")}
              </h2>
              <div className="rounded-md border-l-4 border-blue-400 bg-blue-50 p-3 text-sm text-blue-800">
                {t(
                  "productsPage.documentsAfterSaveNote",
                  "Garanti Belgesi ve Kullanım Kılavuzu, ürünü kaydettikten sonra açılan düzenleme ekranından eklenebilir.",
                )}
              </div>
            </section>
          </form>
        </Form>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-white px-4 py-3 shadow-lg md:px-8">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate("/products/products")}
            className="inline-flex h-11 items-center px-3 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            ← {t("common.cancel", "İptal")}
          </button>
          <CheckRole section="PRODUCTS_LIST" action="EDIT">
            <Button
              size="lg"
              disabled={loading}
              onClick={form.handleSubmit(onSubmit)}
              className="min-w-[140px]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.saving", "Kaydediliyor...")}
                </>
              ) : (
                t("common.save", "Kaydet")
              )}
            </Button>
          </CheckRole>
        </div>
      </div>
    </main>
  );
};

export default ProductCreatePage;
